import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Select,
  Space,
  Upload,
  notification,
} from "antd";
import {
  DeleteOutlined,
  PictureOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import LocalizedInput from "./LocalizedInput";
import { getTrainings } from "store/slices/trainingsSlice";

const EMPTY_LOCALIZED = { fr: "", en: "", ar: "" };

const normalizeStep = (step) => ({
  title: { ...EMPTY_LOCALIZED, ...(step?.title || {}) },
  description: { ...EMPTY_LOCALIZED, ...(step?.description || {}) },
});

const PromotionForm = ({
  form,
  onSubmit,
  formId = "promotion_form",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.promotions);
  const { trainings } = useSelector((state) => state.trainings);
  const mainImage = Form.useWatch("mainImage", form) || [];

  useEffect(() => {
    dispatch(getTrainings({ page: 1, all: true }));
  }, [dispatch]);

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields?.length) {
      form.scrollToField(errorFields[0].name);
    }
    notification.error({
      message: t(
        "promotions.form.validationError",
        "Please fill the required fields"
      ),
    });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      id={formId}
      onFinish={onSubmit}
      onFinishFailed={onFinishFailed}
      disabled={loading}
      style={{ maxWidth: 900, margin: "0 auto" }}
    >
      <LocalizedInput
        name="badge"
        label={t("promotions.form.badge.label")}
        rules={[{ required: true }]}
      />

      <LocalizedInput
        name="mainTitle"
        label={t("promotions.form.mainTitle.label", "Main title")}
        rules={[{ required: true }]}
      />

      <LocalizedInput
        name="mainDescription"
        label={t("promotions.form.mainDescription.label", "Main description")}
        rules={[{ required: true }]}
        textarea
      />

      <Form.Item
        name="mainImage"
        label={t("promotions.form.mainImage.label", "Main image")}
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
      >
        <Upload
          name="mainImage"
          listType="picture-card"
          maxCount={1}
          beforeUpload={() => false}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <PictureOutlined />
            <span>{t("promotions.form.mainImage.upload", "Upload image")}</span>
          </div>
        </Upload>
      </Form.Item>

      {mainImage.length > 0 && (
        <Button
          danger
          type="link"
          icon={<DeleteOutlined />}
          onClick={() => form.setFieldValue("mainImage", [])}
        >
          {t("promotions.form.mainImage.delete", "Delete image")}
        </Button>
      )}

      <LocalizedInput
        name="offerTitle"
        label={t("promotions.form.offerTitle.label", "Offer title")}
        rules={[{ required: true }]}
      />

      <LocalizedInput
        name="offerDescription"
        label={t("promotions.form.offerDescription.label", "Offer description")}
        rules={[{ required: true }]}
        textarea
      />

      <Form.Item
        label={t("promotions.form.formations.label", "Formations")}
        name="formations"
        rules={[{ required: true }]}
      >
        <Select
          mode="multiple"
          placeholder={t(
            "promotions.form.formations.placeholder",
            "Select formations"
          )}
          optionFilterProp="label"
          options={(trainings || []).map((training) => ({
            value: training?._id,
            label: training?.name || training?.title || training?._id,
          }))}
        />
      </Form.Item>

      <LocalizedInput
        name="journeyTitle"
        label={t("promotions.form.journeyTitle.label", "Journey title")}
        rules={[{ required: true }]}
      />

      <LocalizedInput
        name="journeyDescription"
        label={t("promotions.form.journeyDescription.label", "Journey description")}
        rules={[{ required: true }]}
        textarea
      />

      <Divider orientation="left">
        {t("promotions.form.steps.label", "Journey steps")}
      </Divider>

      <Form.List name="journeySteps">
        {(fields, { add, remove }) => (
          <Space direction="vertical" style={{ width: "100%" }} size={16}>
            {fields.map((field, index) => (
              <Card
                key={field.key}
                size="small"
                title={`${t("promotions.form.steps.step", "Step")} ${index + 1}`}
                extra={
                  <Button
                    danger
                    type="link"
                    onClick={() => remove(field.name)}
                    icon={<DeleteOutlined />}
                  >
                    {t("common.delete", "Delete")}
                  </Button>
                }
              >
                <LocalizedInput
                  name={[field.name, "title"]}
                  label={t("promotions.form.steps.title", "Step title")}
                  rules={[{ required: true }]}
                />
                <LocalizedInput
                  name={[field.name, "description"]}
                  label={t(
                    "promotions.form.steps.description",
                    "Step description"
                  )}
                  rules={[{ required: true }]}
                  textarea
                />
              </Card>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add(normalizeStep())}
              block
            >
              {t("promotions.form.steps.add", "Add step")}
            </Button>
          </Space>
        )}
      </Form.List>

      <Form.Item
        label={t("promotions.form.startDate.label")}
        name="startDate"
        rules={[{ required: true }]}
      >
        <DatePicker
          showTime
          style={{ width: "100%" }}
          disabledDate={(d) => d && d < dayjs().startOf("day")}
        />
      </Form.Item>

      <Form.Item
        label={t("promotions.form.endDate.label")}
        name="endDate"
        rules={[{ required: true }]}
      >
        <DatePicker showTime style={{ width: "100%" }} />
      </Form.Item>
    </Form>
  );
};

PromotionForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formId: PropTypes.string,
};

export default PromotionForm;
